import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';

export type GlobalSearchStudent = {
  user_id: string;
  name: string;
  avatar?: string;
  status?: string;
  focus_area?: string;
  goal?: string;
  goals?: string[];
  total_sessions?: number;
  last_session_date?: string;
  next_session_date?: string;
  title?: string;
  duration?: number;
  date?: {
    day?: number;
    month?: string;
    year?: number;
  };
  time?: string;
};

export type GlobalSearchSession = {
  id: string;
  booking_id?: string;
  scheduled_time?: string;
  joining_link?: string;
  meeting_platform?: string;
  status?: string;
  notes?: string;
  title?: string;
  description?: string;
  duration?: number;
  date?: {
    day?: number;
    month?: string;
    year?: number;
  };
  time?: string;
  student_name?: string;
  student_avatar?: string;
};

export type GlobalSearchResponse = {
  students: GlobalSearchStudent[];
  sessions: GlobalSearchSession[];
};

type GlobalSearchState = {
  loading: boolean;
  error: boolean;
  errorMessage: string;
  query: string;
  data: GlobalSearchResponse;
};

const emptyResults: GlobalSearchResponse = { students: [], sessions: [] };

const initialState: GlobalSearchState = {
  loading: false,
  error: false,
  errorMessage: '',
  query: '',
  data: emptyResults,
};

const normalizeSearchResponse = (payload: any): GlobalSearchResponse => {
  const data = payload?.response ?? payload?.data ?? payload ?? {};
  return {
    students: Array.isArray(data?.students) ? data.students : [],
    sessions: Array.isArray(data?.sessions) ? data.sessions : [],
  };
};

export const getGlobalSearchResults = createAsyncThunk<
  { query: string; data: GlobalSearchResponse },
  string
>('globalSearch/getResults', async (query) => {
  const trimmed = query.trim();
  if (!trimmed) {
    return { query: trimmed, data: emptyResults };
  }
  try {
    const { data } = await api.get('/api/coaches/me/search', {
      params: { q: trimmed },
    });
    return { query: trimmed, data: normalizeSearchResponse(data) };
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 404) {
      return { query: trimmed, data: emptyResults };
    }
    throw error;
  }
});

const globalSearchSlice = createSlice({
  name: 'globalSearch',
  initialState,
  reducers: {
    resetGlobalSearch: (state) => {
      state.loading = false;
      state.error = false;
      state.errorMessage = '';
      state.query = '';
      state.data = emptyResults;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getGlobalSearchResults.pending, (state, action) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
      state.query = action.meta.arg.trim();
    });
    builder.addCase(getGlobalSearchResults.fulfilled, (state, action) => {
      if (action.payload.query !== state.query) {
        return;
      }
      state.loading = false;
      state.data = action.payload.data;
    });
    builder.addCase(getGlobalSearchResults.rejected, (state, action) => {
      if (action.meta.arg.trim() !== state.query) {
        return;
      }
      state.loading = false;
      state.error = true;
      state.errorMessage =
        action.error.message ?? 'Failed to load search results.';
      state.data = emptyResults;
    });
  },
});

export const { resetGlobalSearch } = globalSearchSlice.actions;
export default globalSearchSlice.reducer;
